import { useState, useEffect, useCallback } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useLocation, useParams } from "wouter";
import { useTranslation } from "react-i18next";
import { fmtDate, intlLocale } from "@/lib/locale";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import {
  ArrowLeft, Car, Building2, ChevronLeft, ChevronRight,
  ShoppingBag, Edit, Star, MapPin, MessageSquare, X, ZoomIn,
} from "lucide-react";
import type { MarketplaceListingWithDetails, User, DealerReviewsResult, MarketplaceReviewWithDetails, MarketplaceReview } from "@shared/schema";
import { DealerProfileDialog } from "@/components/marketplace/DealerProfileDialog";

const STATUS_COLORS: Record<string, string> = {
  active: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
  pending: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400",
  sold: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400",
  draft: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",
};

function formatPrice(price: string | null | undefined, na: string) {
  if (!price) return na;
  const n = parseFloat(price);
  if (isNaN(n)) return na;
  return new Intl.NumberFormat(intlLocale(), { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);
}

function SpecRow({ label, value }: { label: string; value?: string | number | null }) {
  if (value == null || value === "") return null;
  return (
    <div className="flex flex-col [@media(min-width:400px)]:flex-row [@media(min-width:400px)]:items-center [@media(min-width:400px)]:justify-between px-3 py-2 border-b border-border last:border-0">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-medium [@media(min-width:400px)]:ml-2 min-w-0 break-all">{String(value)}</span>
    </div>
  );
}

function StarRow({ rating, size = "sm" }: { rating: number; size?: "sm" | "md" }) {
  const cls = size === "md" ? "w-4 h-4" : "w-3.5 h-3.5";
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((s) => (
        <Star
          key={s}
          className={`${cls} ${s <= Math.round(rating) ? "fill-amber-400 text-amber-400" : "text-muted-foreground/20"}`}
        />
      ))}
    </div>
  );
}

function StarPicker({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  const [hovered, setHovered] = useState(0);
  const display = hovered || value;
  return (
    <div className="flex items-center gap-1" data-testid="star-picker">
      {[1, 2, 3, 4, 5].map((s) => (
        <button
          key={s}
          type="button"
          data-testid={`star-${s}`}
          onClick={() => onChange(s)}
          onMouseEnter={() => setHovered(s)}
          onMouseLeave={() => setHovered(0)}
          className="p-0.5 focus:outline-none"
          aria-label={`${s} star${s !== 1 ? "s" : ""}`}
        >
          <Star
            className={`w-7 h-7 transition-colors ${
              s <= display ? "fill-amber-400 text-amber-400" : "text-muted-foreground/20 hover:text-amber-300"
            }`}
          />
        </button>
      ))}
    </div>
  );
}

function ReviewCard({ review }: { review: MarketplaceReviewWithDetails }) {
  const displayName = review.reviewerDealershipName || review.reviewerUsername;
  const date = review.createdAt
    ? fmtDate(new Date(review.createdAt as unknown as string), { year: "numeric", month: "short", day: "numeric" })
    : "—";
  return (
    <div className="p-4 rounded-lg border border-border bg-card space-y-2">
      <div className="flex items-center justify-between gap-2">
        <span className="font-medium text-sm" data-testid={`review-reviewer-${review.id}`}>{displayName}</span>
        <span className="text-xs text-muted-foreground shrink-0" data-testid={`review-date-${review.id}`}>{date}</span>
      </div>
      <StarRow rating={review.rating} />
      {review.reviewText && (
        <p className="text-sm text-muted-foreground leading-relaxed" data-testid={`review-text-${review.id}`}>
          {review.reviewText}
        </p>
      )}
    </div>
  );
}

function AllReviewsModal({
  sellerUserId,
  sellerName,
  open,
  onClose,
}: {
  sellerUserId: string;
  sellerName: string;
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation(["marketplace", "common"]);
  const { data, isLoading } = useQuery<DealerReviewsResult>({
    queryKey: ["/api/marketplace/dealers", sellerUserId, "reviews"],
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/marketplace/dealers/${sellerUserId}/reviews`);
      return res.json();
    },
    enabled: open && !!sellerUserId,
  });

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[80vh] flex flex-col" data-testid="dialog-all-reviews">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
            {t("marketplace:allReviews.title", { name: sellerName })}
          </DialogTitle>
          {data && data.avgRating != null && (
            <div className="flex items-center gap-3 pt-1">
              <span className="text-2xl font-bold text-amber-500" data-testid="text-modal-avg-rating">
                {data.avgRating.toFixed(1)}
              </span>
              <StarRow rating={data.avgRating} size="md" />
              <span className="text-sm text-muted-foreground" data-testid="text-modal-review-count">
                {t("marketplace:allReviews.reviews", { count: data.totalCount })}
              </span>
            </div>
          )}
        </DialogHeader>
        <div className="flex-1 overflow-y-auto space-y-3 pr-1" data-testid="list-all-reviews">
          {isLoading ? (
            Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-24 rounded-lg" />)
          ) : data && data.reviews.length > 0 ? (
            data.reviews.map((review) => <ReviewCard key={review.id} review={review} />)
          ) : (
            <div className="text-center py-10 text-muted-foreground">
              <Star className="w-8 h-8 mx-auto mb-2 text-muted-foreground/20" />
              <p className="text-sm">{t("marketplace:allReviews.noReviews")}</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function LeaveReviewDialog({
  listingId,
  sellerName,
  open,
  onClose,
}: {
  listingId: string;
  sellerName: string;
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation(["marketplace", "common"]);
  const { toast } = useToast();
  const [rating, setRating] = useState(0);
  const [reviewText, setReviewText] = useState("");
  const ratingLabels = t("marketplace:leaveReview.ratingLabels", { returnObjects: true }) as string[];

  const mutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/marketplace/${listingId}/review`, {
        rating,
        reviewText: reviewText.trim() || null,
      });
      if (!res.ok) {
        const body = await res.json();
        throw new Error(body.message || t("marketplace:leaveReview.couldNotSubmit"));
      }
      return res.json();
    },
    onSuccess: () => {
      toast({ title: t("marketplace:leaveReview.reviewSubmitted"), description: t("marketplace:leaveReview.reviewSubmittedThanks") });
      queryClient.invalidateQueries({ queryKey: ["/api/marketplace", listingId, "buyer-status"] });
      queryClient.invalidateQueries({ queryKey: ["/api/marketplace", listingId] });
      onClose();
    },
    onError: (err: Error) => {
      toast({ title: t("marketplace:leaveReview.couldNotSubmit"), description: err.message, variant: "destructive" });
    },
  });

  function handleClose() {
    if (!mutation.isPending) {
      setRating(0);
      setReviewText("");
      onClose();
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md" data-testid="dialog-review">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
            {t("marketplace:leaveReview.titleDetail")}
          </DialogTitle>
          <DialogDescription>
            {t("marketplace:leaveReview.descriptionDetail", { name: sellerName })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <p className="text-sm font-medium">{t("marketplace:leaveReview.yourRating")} <span className="text-destructive">*</span></p>
            <StarPicker value={rating} onChange={setRating} />
            {rating > 0 && (
              <p className="text-xs text-muted-foreground" data-testid="text-rating-label">
                {ratingLabels[rating]}
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <p className="text-sm font-medium">{t("marketplace:leaveReview.reviewLabel")} <span className="text-muted-foreground font-normal">{t("marketplace:leaveReview.optional")}</span></p>
            <Textarea
              data-testid="input-review-text"
              placeholder={t("marketplace:leaveReview.placeholder")}
              value={reviewText}
              onChange={(e) => setReviewText(e.target.value)}
              rows={4}
              maxLength={2000}
              disabled={mutation.isPending}
            />
            <p className="text-xs text-muted-foreground text-right">{reviewText.length}/2000</p>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={mutation.isPending}
            data-testid="button-skip-review"
          >
            {t("marketplace:leaveReview.cancel")}
          </Button>
          <Button
            onClick={() => mutation.mutate()}
            disabled={rating === 0 || mutation.isPending}
            data-testid="button-submit-review"
          >
            {mutation.isPending ? t("marketplace:leaveReview.submitting") : t("marketplace:leaveReview.submit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

type BuyerStatus = {
  hasContacted: boolean;
  existingReview: MarketplaceReview | null;
};

export default function MarketplaceDetailPage() {
  const { t } = useTranslation(["marketplace", "common"]);
  const params = useParams<{ id: string }>();
  const [, navigate] = useLocation();
  const [photoIdx, setPhotoIdx] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState(0);
  const [showReviews, setShowReviews] = useState(false);
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [showSellerProfile, setShowSellerProfile] = useState(false);

  const openLightbox = useCallback((idx: number) => {
    setLightboxIdx(idx);
    setLightboxOpen(true);
  }, []);

  const closeLightbox = useCallback(() => setLightboxOpen(false), []);

  const { data: listing, isLoading } = useQuery<MarketplaceListingWithDetails>({
    queryKey: ["/api/marketplace", params.id],
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/marketplace/${params.id}`);
      return res.json();
    },
    enabled: !!params.id,
  });

  const { toast } = useToast();
  const { data: me } = useQuery<Pick<User, "id">>({ queryKey: ["/api/auth/me"], retry: false });
  const isOwner = me && listing && me.id === listing.sellerUserId;

  const photoCount = listing?.photos.length ?? 1;
  useEffect(() => {
    if (!lightboxOpen) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") { closeLightbox(); return; }
      if (e.key === "ArrowRight") setLightboxIdx((i) => (i + 1) % photoCount);
      if (e.key === "ArrowLeft") setLightboxIdx((i) => (i - 1 + photoCount) % photoCount);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightboxOpen, closeLightbox, photoCount]);

  const inquireMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/marketplace/${params.id}/inquire`);
      if (!res.ok) {
        const body = await res.json();
        throw new Error(body.message || t("marketplace:detailToast.couldNotStart"));
      }
      return res.json();
    },
    onSuccess: (conv) => {
      // Navigate to the messages inbox with this conversation open
      navigate(`/marketplace/messages?conv=${conv.id}`);
    },
    onError: (err: Error) => {
      toast({ title: t("marketplace:detailToast.couldNotStart"), description: err.message, variant: "destructive" });
    },
  });

  const { data: buyerStatus } = useQuery<BuyerStatus>({
    queryKey: ["/api/marketplace", params.id, "buyer-status"],
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/marketplace/${params.id}/buyer-status`);
      return res.json();
    },
    enabled: !!params.id && !!me && !isOwner && listing?.status === "sold",
  });

  const canReview = !isOwner && listing?.status === "sold" && buyerStatus?.hasContacted && !buyerStatus?.existingReview;
  const existingReview = buyerStatus?.existingReview;

  function goBack() {
    const searchParams = new URLSearchParams(window.location.search);
    const back = searchParams.get("back");
    if (back) {
      navigate(back);
    } else {
      navigate("/marketplace");
    }
  }

  if (isLoading) {
    return (
      <div className="max-w-5xl mx-auto p-6 space-y-6">
        <Skeleton className="h-8 w-40" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <Skeleton className="aspect-video rounded-xl" />
          <div className="space-y-4">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-6 w-32" />
            <Skeleton className="h-32 w-full" />
          </div>
        </div>
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="max-w-5xl mx-auto p-6">
        <Button variant="ghost" onClick={goBack} data-testid="button-back">
          <ArrowLeft className="w-4 h-4 mr-1.5" />{t("marketplace:detail.back")}
        </Button>
        <div className="mt-12 text-center">
          <ShoppingBag className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
          <p className="text-muted-foreground">{t("marketplace:detail.listingNotFound")}</p>
        </div>
      </div>
    );
  }

  const photos = listing.photos;
  const currentPhoto = photos[photoIdx];
  const sellerName = listing.sellerDealershipName || listing.sellerUsername;

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6">
      {/* Back nav */}
      <div className="flex items-center justify-between">
        <Button
          variant="ghost"
          size="sm"
          data-testid="button-back-marketplace"
          onClick={goBack}
        >
          <ArrowLeft className="w-4 h-4 mr-1.5" />
          {t("marketplace:detail.backToMarketplace")}
        </Button>
        {isOwner && (
          <Button
            variant="outline"
            size="sm"
            data-testid="button-edit-listing"
            onClick={() => navigate(`/marketplace/${listing.id}/edit`)}
          >
            <Edit className="w-3.5 h-3.5 mr-1.5" />
            {t("marketplace:detail.editListing")}
          </Button>
        )}
      </div>

      {/* Title + status — above the photo */}
      <div className="flex items-start gap-3">
        <h1 className="text-2xl font-bold leading-tight flex-1" data-testid="text-listing-title">
          {listing.year} {listing.make} {listing.model}
          {listing.trim ? ` ${listing.trim}` : ""}
        </h1>
        <Badge className={`text-xs shrink-0 mt-1 ${STATUS_COLORS[listing.status]}`} data-testid="badge-status">
          {listing.status === "pending" ? t("marketplace:status.pendingSale") : t(`marketplace:status.${listing.status}`)}
        </Badge>
      </div>
      {listing.bodyStyle && (
        <p className="text-muted-foreground text-sm -mt-4">{listing.bodyStyle}</p>
      )}

      {/* Sold banner */}
      {listing.status === "sold" && (
        <div
          data-testid="banner-sold"
          className="flex items-center gap-3 rounded-xl border border-gray-300 dark:border-gray-700 bg-gray-100 dark:bg-gray-800/60 px-4 py-3"
        >
          <ShoppingBag className="w-5 h-5 text-gray-500 dark:text-gray-400 shrink-0" />
          <div>
            <p className="font-semibold text-sm text-gray-800 dark:text-gray-200">{t("marketplace:detail.soldTitle")}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">{t("marketplace:detail.soldSubtitle")}</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
        {/* Left: Photo gallery */}
        <div className="lg:col-span-3 space-y-3">
          {/* Hero photo */}
          <div className="relative aspect-video rounded-xl overflow-hidden bg-muted group">
            {currentPhoto ? (
              <img
                src={currentPhoto.photoUrl}
                alt={`${listing.year} ${listing.make} ${listing.model} — photo ${photoIdx + 1}`}
                className="w-full h-full object-cover cursor-zoom-in"
                data-testid="img-hero-photo"
                onClick={() => openLightbox(photoIdx)}
                onError={(e) => {
                  const t = e.currentTarget;
                  t.style.display = "none";
                  const ph = t.nextElementSibling as HTMLElement | null;
                  if (ph) ph.style.display = "flex";
                }}
              />
            ) : null}
            {/* Always mounted so onError can reveal it */}
            <div
              className="w-full h-full items-center justify-center"
              data-testid="img-hero-placeholder"
              style={{ display: currentPhoto ? "none" : "flex" }}
            >
              <Car className="w-16 h-16 text-muted-foreground/20" />
            </div>
            {/* Zoom hint */}
            {currentPhoto && (
              <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                <div className="bg-black/50 text-white rounded p-1.5">
                  <ZoomIn className="w-3.5 h-3.5" />
                </div>
              </div>
            )}
            {currentPhoto && photos.length > 1 && (
              <>
                <button
                  type="button"
                  data-testid="button-prev-photo"
                  onClick={(e) => { e.stopPropagation(); setPhotoIdx((i) => (i - 1 + photos.length) % photos.length); }}
                  className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70 transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  data-testid="button-next-photo"
                  onClick={(e) => { e.stopPropagation(); setPhotoIdx((i) => (i + 1) % photos.length); }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70 transition-colors"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
                <span className="absolute bottom-2 right-2 bg-black/50 text-white text-xs px-2 py-1 rounded">
                  {photoIdx + 1} / {photos.length}
                </span>
              </>
            )}
          </div>

          {/* Thumbnails */}
          {photos.length > 1 && (
            <div className="flex gap-2 overflow-x-auto pb-1">
              {photos.map((photo, idx) => (
                <button
                  key={photo.id}
                  type="button"
                  data-testid={`button-thumbnail-${idx}`}
                  onClick={() => setPhotoIdx(idx)}
                  className={`shrink-0 w-16 h-12 rounded overflow-hidden border-2 transition-colors ${
                    idx === photoIdx ? "border-violet-500" : "border-transparent"
                  }`}
                >
                  <img
                    src={photo.photoUrl}
                    alt=""
                    className="w-full h-full object-cover"
                    onError={(e) => { e.currentTarget.style.visibility = "hidden"; }}
                  />
                </button>
              ))}
            </div>
          )}

          {/* Description */}
          {listing.description && (
            <div>
              <h3 className="font-semibold text-sm mb-1">{t("marketplace:detail.description")}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed" data-testid="text-description">
                {listing.description}
              </p>
            </div>
          )}

          {/* Recon Notes */}
          {listing.reconNotes && (
            <div>
              <h3 className="font-semibold text-sm mb-1">{t("marketplace:detail.reconNotes")}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed bg-muted/50 rounded-lg p-3" data-testid="text-recon-notes">
                {listing.reconNotes}
              </p>
            </div>
          )}
        </div>

        {/* Right: Details panel */}
        <div className="lg:col-span-2 space-y-5">
          {/* Asking price */}
          <div className="p-4 rounded-xl bg-violet-500/10 border border-violet-500/20">
            <p className="text-xs text-muted-foreground mb-1">{t("marketplace:detail.askingPrice")}</p>
            <p className="text-3xl font-bold text-violet-600 dark:text-violet-400" data-testid="text-asking-price">
              {formatPrice(listing.askingPrice, t("marketplace:detail.priceNA"))}
            </p>
          </div>

          {/* Quick specs */}
          <div className="rounded-xl border border-border divide-y divide-border">
            <SpecRow label={t("marketplace:detail.vin")} value={listing.vin} />
            <SpecRow label={t("marketplace:detail.mileage")} value={listing.mileage != null ? t("marketplace:units.miles", { count: listing.mileage }) : null} />
            <SpecRow label={t("marketplace:detail.color")} value={listing.color} />
            <SpecRow label={t("marketplace:detail.condition")} value={listing.condition ? t(`marketplace:condition.${listing.condition}`) : null} />
            <SpecRow label={t("marketplace:detail.daysListed")} value={listing.daysListed === 0 ? t("marketplace:detail.today") : t("marketplace:detail.days", { count: listing.daysListed })} />
          </div>

          {/* Seller profile card */}
          <div className="rounded-xl border border-border p-4 space-y-3" data-testid="card-seller-profile">
            <h3 className="text-sm font-semibold">{t("marketplace:detail.sellingDealer")}</h3>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-violet-500/15 border border-violet-500/20 flex items-center justify-center shrink-0">
                <Building2 className="w-5 h-5 text-violet-500" />
              </div>
              <div className="flex-1 min-w-0">
                <button
                  type="button"
                  data-testid="button-seller-profile"
                  onClick={() => setShowSellerProfile(true)}
                  className="font-medium text-sm text-left hover:text-violet-600 dark:hover:text-violet-400 hover:underline transition-colors"
                >
                  {sellerName}
                </button>
                {listing.sellerDealershipName && (
                  <p className="text-xs text-muted-foreground">@{listing.sellerUsername}</p>
                )}
                {(listing.sellerCity || listing.sellerState) && (
                  <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5" data-testid="text-seller-location">
                    <MapPin className="w-3 h-3 shrink-0" />
                    {[listing.sellerCity, listing.sellerState].filter(Boolean).join(", ")}
                  </p>
                )}
                {listing.sellerAvgRating != null && listing.sellerReviewCount > 0 ? (
                  <div className="mt-2 space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-amber-600 dark:text-amber-400" data-testid="text-seller-avg-rating">
                        {listing.sellerAvgRating.toFixed(1)}
                      </span>
                      <StarRow rating={listing.sellerAvgRating} />
                      <span className="text-xs text-muted-foreground" data-testid="text-seller-review-count">
                        ({listing.sellerReviewCount})
                      </span>
                    </div>
                    <button
                      type="button"
                      data-testid="button-see-all-reviews"
                      onClick={() => setShowReviews(true)}
                      className="text-xs text-violet-600 dark:text-violet-400 hover:underline"
                    >
                      {t("marketplace:detail.seeAllReviews")}
                    </button>
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground mt-1">{t("marketplace:detail.noReviews")}</p>
                )}
              </div>
            </div>

            {/* Message Seller button — shown to non-owners on non-sold listings */}
            {!isOwner && listing.status !== "sold" && (
              <div className="pt-2 border-t border-border">
                <Button
                  className="w-full gap-2 bg-violet-600 hover:bg-violet-700 text-white"
                  data-testid="button-inquire"
                  disabled={inquireMutation.isPending}
                  onClick={() => inquireMutation.mutate()}
                >
                  <MessageSquare className="w-4 h-4" />
                  {inquireMutation.isPending ? t("marketplace:detail.opening") : t("marketplace:detail.messageSeller")}
                </Button>
              </div>
            )}

            {/* Buyer review prompt — shown only when listing is sold and user previously contacted the seller */}
            {canReview && (
              <div className="pt-2 border-t border-border">
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full gap-1.5"
                  data-testid="button-rate-seller"
                  onClick={() => setShowReviewDialog(true)}
                >
                  <Star className="w-3.5 h-3.5" />
                  {t("marketplace:detail.rateSeller")}
                </Button>
              </div>
            )}

            {/* Show existing review if buyer already reviewed */}
            {existingReview && (
              <div className="pt-2 border-t border-border space-y-1.5" data-testid="section-your-review">
                <p className="text-xs font-medium text-muted-foreground">{t("marketplace:detail.yourReview")}</p>
                <StarRow rating={existingReview.rating} />
                {existingReview.reviewText && (
                  <p className="text-sm text-muted-foreground leading-relaxed" data-testid="text-your-review">
                    {existingReview.reviewText}
                  </p>
                )}
              </div>
            )}
          </div>

          <p className="text-xs text-muted-foreground">
            {listing.daysListed === 0 ? t("marketplace:detail.listedToday") : t("marketplace:detail.listedDaysAgo", { count: listing.daysListed })}
          </p>
        </div>
      </div>

      {/* All reviews modal */}
      <AllReviewsModal
        sellerUserId={listing.sellerUserId}
        sellerName={sellerName}
        open={showReviews}
        onClose={() => setShowReviews(false)}
      />

      {/* Leave a review dialog */}
      <LeaveReviewDialog
        listingId={listing.id}
        sellerName={sellerName}
        open={showReviewDialog}
        onClose={() => setShowReviewDialog(false)}
      />

      {/* Seller profile popup */}
      <DealerProfileDialog
        userId={listing.sellerUserId}
        displayName={sellerName}
        open={showSellerProfile}
        onClose={() => setShowSellerProfile(false)}
      />

      {/* Lightbox */}
      {lightboxOpen && photos.length > 0 && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90"
          data-testid="lightbox-overlay"
          onClick={closeLightbox}
        >
          {/* Close */}
          <button
            type="button"
            data-testid="button-lightbox-close"
            onClick={closeLightbox}
            className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Counter */}
          {photos.length > 1 && (
            <span className="absolute top-4 left-1/2 -translate-x-1/2 bg-black/50 text-white text-sm px-3 py-1 rounded-full">
              {lightboxIdx + 1} / {photos.length}
            </span>
          )}

          {/* Image */}
          <img
            src={photos[lightboxIdx].photoUrl}
            alt={`${listing.year} ${listing.make} ${listing.model} — photo ${lightboxIdx + 1}`}
            className="max-w-[90vw] max-h-[85vh] object-contain rounded-lg shadow-2xl"
            data-testid="img-lightbox"
            onClick={(e) => e.stopPropagation()}
          />

          {/* Prev / Next */}
          {photos.length > 1 && (
            <>
              <button
                type="button"
                data-testid="button-lightbox-prev"
                onClick={(e) => { e.stopPropagation(); setLightboxIdx((i) => (i - 1 + photos.length) % photos.length); }}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                type="button"
                data-testid="button-lightbox-next"
                onClick={(e) => { e.stopPropagation(); setLightboxIdx((i) => (i + 1) % photos.length); }}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </>
          )}

          {/* Thumbnail strip */}
          {photos.length > 1 && (
            <div
              className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 max-w-[90vw] overflow-x-auto px-2"
              onClick={(e) => e.stopPropagation()}
            >
              {photos.map((photo, idx) => (
                <button
                  key={photo.id}
                  type="button"
                  data-testid={`button-lightbox-thumb-${idx}`}
                  onClick={() => setLightboxIdx(idx)}
                  className={`shrink-0 w-14 h-10 rounded overflow-hidden border-2 transition-colors ${
                    idx === lightboxIdx ? "border-white" : "border-white/30 hover:border-white/60"
                  }`}
                >
                  <img src={photo.photoUrl} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}

    </div>
  );
}
